import { useEffect, useRef } from 'react'

/**
 * La tecla «+» agrega el pago que está cargado.
 *
 * Es la otra mitad del cobro sin mouse. Con un solo medio el pago se da por
 * entendido y alcanza con Enter (ver `pagoImplicito`), pero el cobro DIVIDIDO
 * —mitad efectivo, mitad débito, que es lo que pasa cuando al cliente no le
 * alcanza el efectivo— obligaba a tocar un botón con el dedo en medio de la
 * operación.
 *
 * Está en un hook y no escrito dos veces porque las DOS pantallas que cobran
 * lo necesitan, y el criterio de cuándo la tecla es nuestra es el mismo que ya
 * costó afinar en `useEnterConfirma`: con el foco en un campo de verdad —la
 * referencia de un cupón— el «+» es parte de lo que se está escribiendo.
 *
 * Es la misma tecla que suma una unidad en el ticket del POS, y no se pisan:
 * en las dos «+» quiere decir «uno más», y nunca están las dos en pantalla.
 */
export function useTeclaAgregar(accion: () => void, { activo }: { activo: boolean }) {
  // Por lo mismo que `useEnterConfirma`: la acción se recrea en cada render
  // porque depende del importe y del medio, y re-suscribir el listener en cada
  // dígito que se toca es trabajo por nada.
  const ref = useRef(accion)

  useEffect(() => {
    ref.current = accion
  })

  useEffect(() => {
    if (!activo) return

    function alApretar(e: KeyboardEvent) {
      if (e.key !== '+' || e.ctrlKey || e.altKey || e.metaKey) return

      const foco = document.activeElement
      const editable =
        (foco instanceof HTMLInputElement && !foco.readOnly && !foco.disabled) ||
        foco instanceof HTMLTextAreaElement ||
        (foco instanceof HTMLElement && foco.isContentEditable)

      if (editable) return

      e.preventDefault()
      ref.current()
    }

    window.addEventListener('keydown', alApretar)
    return () => window.removeEventListener('keydown', alApretar)
  }, [activo])
}

/**
 * Suelta el foco del botón que acaba de hacer algo.
 *
 * Después de agregar un pago el foco queda en el botón que lo agregó —o en el
 * billete al que se llegó con Tab— y desde ahí el Enter es DE ESE BOTÓN: el
 * navegador lo activa por su cuenta y `useEnterConfirma` lo respeta a
 * propósito. El resultado era un cobro dividido que se armaba entero con el
 * teclado y después no se podía cerrar: el Enter volvía a apretar el billete
 * de $1.000 en lugar de cobrar.
 *
 * Soltarlo deja el foco en el diálogo, que es donde tiene que estar para que
 * la tecla siguiente sea del cobro: los dígitos cargan el importe, «+» agrega
 * y Enter cobra.
 */
export function soltarElFoco() {
  const foco = document.activeElement

  if (foco instanceof HTMLButtonElement) foco.blur()
}
